import React from 'react'
import { User, Bot } from 'lucide-react'
import { useThemeColors } from '../../hooks/useThemeColors'
import ReactMarkdown from 'react-markdown'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: Record<string, unknown>
}

interface ChatMessageProps {
  message: Message
}

export default function ChatMessage({ message }: ChatMessageProps) {
  const { colors } = useThemeColors()
  const isUser = message.role === 'user'

  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
      flexDirection: isUser ? 'row-reverse' : 'row'
    }}>
      {/* Avatar */}
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: isUser ? colors.primary : colors.primaryAlpha20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {isUser ? (
          <User size={18} style={{ color: colors.background }} />
        ) : (
          <Bot size={18} style={{ color: colors.primary }} />
        )}
      </div>

      {/* Message Content */}
      <div style={{
        flex: 1,
        maxWidth: '85%'
      }}>
        <div style={{
          backgroundColor: isUser ? colors.primary : colors.inputBackground,
          color: isUser ? colors.background : colors.text,
          padding: '12px 16px',
          borderRadius: '12px',
          borderTopRightRadius: isUser ? '4px' : '12px',
          borderTopLeftRadius: isUser ? '12px' : '4px'
        }}>
          {isUser ? (
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
              {message.content}
            </p>
          ) : (
            <div className="ai-message-content" style={{ fontSize: '14px', lineHeight: '1.5' }}>
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
                  ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
                  li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                  code: ({ inline, children }) => 
                    inline ? (
                      <code style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontSize: '13px'
                      }}>{children}</code>
                    ) : (
                      <pre style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        padding: '12px',
                        borderRadius: '6px',
                        overflowX: 'auto',
                        margin: '8px 0'
                      }}>
                        <code style={{ fontSize: '13px' }}>{children}</code>
                      </pre>
                    ),
                  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                  h3: ({ children }) => <h3 style={{ margin: '12px 0 8px 0', fontSize: '16px', fontWeight: 600 }}>{children}</h3>,
                  h4: ({ children }) => <h4 style={{ margin: '8px 0 4px 0', fontSize: '15px', fontWeight: 600 }}>{children}</h4>,
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      borderLeft: `3px solid ${colors.primary}`,
                      paddingLeft: '12px',
                      margin: '8px 0',
                      color: colors.textSecondary
                    }}>{children}</blockquote>
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        <div style={{
          fontSize: '11px',
          color: colors.textSecondary,
          marginTop: '4px',
          textAlign: isUser ? 'right' : 'left'
        }}>
          {new Date(message.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
    </div>
  )
}